import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  Text,
  Button,
  Switch,
  Alert,
  Card,
  TextInput,
  Select,
  Tabs,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  IconAlertCircle,
  IconDeviceTv,
  IconSettings,
  IconPhoto,
  IconBuildingStore,
  IconRocket,
  IconEye,
} from '@tabler/icons-react';
import { APP_STATE_TYPE_ID, APP_CONSTRAINTS, APP_QUERY_KEYS } from '@/lib/constants/app';
import { ROKU_GENRES } from '@/lib/constants/rokuStore';
import { useUpdateAppRoku, useUpsertAppListing } from '../useAppMutations';
import { AppUtils } from '../AppUtils';
import { StoreAccountUtils } from '../StoreAccountUtils';
import { appRokuFormSchema, type AppRokuFormSchemaData } from '../schemas/AppRokuForm.schema';
import {
  appListingFormSchema,
  type AppListingFormSchemaData,
} from '../schemas/AppListingForm.schema';
import { AppRokuSetupWizard, type WizardStep } from './AppRokuSetupWizard';
import { AppPlatformStatusTab } from './AppPlatformStatusTab';
import { AppRokuImagesPanel } from './AppRokuImagesPanel';
import { AppTvPreview } from './AppTvPreview';
import { AppListingFields } from './AppListingFields';
import type { AppCfgsDto, AppRokuFormData, AppListingFormData } from '@/lib/types/App';
import { FormSaveBar } from '@/shared/components/FormSaveBar';

interface AppRokuTabProps {
  appCfgId: number;
  app: AppCfgsDto;
  isAppActive: boolean;
  rokuAllowed: boolean;
}

export function AppRokuTab({ appCfgId, app, isAppActive, rokuAllowed }: AppRokuTabProps) {
  const [wizardOpen, setWizardOpen] = useState(false);
  // After setup the wizard reopens at one store-onboarding step (privacy policy,
  // store account) from the Status tab's readiness chips.
  const [wizardStep, setWizardStep] = useState<WizardStep | null>(null);
  const updateRokuMutation = useUpdateAppRoku(appCfgId);

  const roku = app.roku;

  // Fail-closed licensing gate (owner decision 2026-08-19): WITHOUT the
  // account's Roku entitlement, the ENTIRE surface is off — including
  // management of a pre-existing (legacy V1) roku row. The server stays the
  // authority on writes; this keeps the UI honest about the license.
  if (!rokuAllowed) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="orange">
        Your current plan does not include the Roku app platform. Contact support to add
        Roku app service to your account.
      </Alert>
    );
  }

  // ── No Roku platform yet — show enable card (wizard handles domain + listing) ──

  if (!roku) {
    return (
      <>
        <Card withBorder p="xl" radius="md">
          <Stack align="center" gap="md">
            <IconDeviceTv size={48} color="var(--mantine-color-violet-5)" />
            <Text fw={600} size="lg">
              Roku App
            </Text>
            <Text c="dimmed" ta="center" maw={400}>
              Bring your content to the biggest screen in the house. Enable a Roku channel to
              reach your audience on their TV.
            </Text>
            <Button onClick={() => setWizardOpen(true)} disabled={!isAppActive}>
              Set Up Roku App
            </Button>
            {!isAppActive && (
              <Text size="sm" c="orange">
                Activate the app first to enable the Roku platform.
              </Text>
            )}
          </Stack>
        </Card>

        <AppRokuSetupWizard
          opened={wizardOpen}
          app={app}
          onClose={() => setWizardOpen(false)}
          onFinished={() => setWizardOpen(false)}
        />
      </>
    );
  }

  // ── Roku platform exists — Settings | Store Listing | Images sub-tabs ──

  return (
    <Tabs defaultValue="settings" variant="pills">
      <Tabs.List mb="md">
        <Tabs.Tab value="settings" leftSection={<IconSettings size={14} />}>
          Settings
        </Tabs.Tab>
        <Tabs.Tab value="listing" leftSection={<IconBuildingStore size={14} />}>
          Store Listing
        </Tabs.Tab>
        <Tabs.Tab value="images" leftSection={<IconPhoto size={14} />}>
          Images
        </Tabs.Tab>
        <Tabs.Tab value="status" leftSection={<IconRocket size={14} />}>
          Status
        </Tabs.Tab>
        <Tabs.Tab value="preview" leftSection={<IconEye size={14} />}>
          Preview
        </Tabs.Tab>
        {/* The store-setup steps (privacy policy, store account) stay reachable after the
            channel exists — the same wizard the Store readiness chips open, from the strip. */}
        <Button
          size="xs"
          variant="light"
          ml="auto"
          leftSection={<IconBuildingStore size={14} />}
          onClick={() => setWizardStep('privacy')}
          disabled={!isAppActive}
        >
          Store Setup
        </Button>
      </Tabs.List>

      <Tabs.Panel value="settings" keepMounted>
        <RokuSettingsForm
          roku={roku}
          isAppActive={isAppActive}
          updateMutation={updateRokuMutation}
        />
      </Tabs.Panel>

      <Tabs.Panel value="listing" keepMounted>
        <StoreListingForm appCfgId={appCfgId} app={app} isAppActive={isAppActive} />
      </Tabs.Panel>

      <Tabs.Panel value="images">
        {/* One writer of the deliverable store (ADR-0017): both arms go
            through factory asset sets — generate (Brand Kit) or manual
            per-slot uploads — and only Approve & Publish goes live. */}
        <AppRokuImagesPanel appCfgId={appCfgId} disabled={!isAppActive} />
      </Tabs.Panel>

      <Tabs.Panel value="status">
        <AppPlatformStatusTab
          appCfgId={appCfgId}
          app={app}
          platform="roku"
          store="roku"
          readinessStores={StoreAccountUtils.storesForApp(app)}
          legacyBuildVersions={app.buildVersions.appBuildVersions}
          onOpenWizard={setWizardStep}
        />
      </Tabs.Panel>

      <Tabs.Panel value="preview">
        <AppTvPreview app={app} />
      </Tabs.Panel>

      <AppRokuSetupWizard
        opened={wizardStep != null}
        app={app}
        initialStep={wizardStep ?? undefined}
        onClose={() => setWizardStep(null)}
        onFinished={() => setWizardStep(null)}
      />
    </Tabs>
  );
}

// ── Internal: Roku Settings Form ──

interface RokuSettingsFormProps {
  roku: NonNullable<AppCfgsDto['roku']>;
  isAppActive: boolean;
  updateMutation: ReturnType<typeof useUpdateAppRoku>;
}

function RokuSettingsForm({ roku, isAppActive, updateMutation }: RokuSettingsFormProps) {
  const disabled = !isAppActive;
  const isRokuActive = roku.core.appStateTypeId === APP_STATE_TYPE_ID.Active;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<AppRokuFormSchemaData>({
    resolver: zodResolver(appRokuFormSchema),
    defaultValues: AppUtils.transformRokuToFormData(roku),
  });

  // Sync form when roku data changes (after mutation success)
  useEffect(() => {
    reset(AppUtils.transformRokuToFormData(roku));
  }, [roku, reset]);

  const { field: titleField } = useController({ control, name: 'title' });
  const { field: adUrlField } = useController({ control, name: 'adUrl' });
  const { field: genresField } = useController({ control, name: 'genres' });
  const { field: rokuStateField } = useController({ control, name: 'appStateTypeId' });

  const handleSave = handleSubmit((data: AppRokuFormSchemaData) => {
    const req = AppUtils.transformRokuFormToUpdateReq(data as AppRokuFormData, roku);
    updateMutation.mutate(req, {
      onSuccess: () => {
        // Clean baseline immediately — the refetch-driven effect reset arrives
        // later; without this the save bar stays up for the whole round-trip.
        reset(data);
        notifications.show({
          title: 'Saved',
          message: 'Roku settings updated successfully.',
          color: 'green',
        });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  });

  const handleDiscard = useCallback(() => {
    reset(AppUtils.transformRokuToFormData(roku));
  }, [roku, reset]);

  return (
    <form onSubmit={handleSave}>
      <FormSaveBar
        isDirty={isDirty}
        isPending={updateMutation.isPending}
        onDiscard={handleDiscard}
        saveDisabled={disabled}
      />

      {disabled && (
        <Alert icon={<IconAlertCircle size={16} />} color="orange" mt="md">
          Roku settings are read-only while the parent app is inactive. Activate the app to
          make changes.
        </Alert>
      )}

      <Stack gap="lg" mt="md">
        <Switch
          label="Roku App Active"
          description={
            isRokuActive
              ? 'Roku platform is enabled for this app'
              : 'Roku platform is disabled for this app'
          }
          checked={rokuStateField.value === APP_STATE_TYPE_ID.Active}
          onChange={(e) =>
            rokuStateField.onChange(
              e.currentTarget.checked ? APP_STATE_TYPE_ID.Active : APP_STATE_TYPE_ID.Inactive,
            )
          }
          disabled={disabled}
        />

        <TextInput
          label="Channel Name"
          description="Shown in the Roku Channel Store and on the home screen"
          value={titleField.value}
          onChange={titleField.onChange}
          onBlur={titleField.onBlur}
          maxLength={APP_CONSTRAINTS.ROKU_TITLE_MAX}
          error={errors.title?.message}
          disabled={disabled}
        />

        <Text fw={600} size="md">
          Advertising
        </Text>

        <TextInput
          label="Roku Ad URL"
          placeholder="https://..."
          description="VAST/VMAP ad tag used only by your Roku app. This is separate from the player ad settings, which apply to web embeds only."
          value={adUrlField.value}
          onChange={adUrlField.onChange}
          onBlur={adUrlField.onBlur}
          maxLength={APP_CONSTRAINTS.LISTING_URL_MAX}
          error={errors.adUrl?.message}
          disabled={disabled}
        />

        <Text fw={600} size="md">
          Store Category
        </Text>

        {/* Curated portal picklist (rokuStore.ts); the column stays TEXT,
            so a legacy/drifted stored value still renders via data merge.
            Single-select — the portal's Category form takes exactly one. */}
        <Select
          label="Category"
          placeholder="Select a category"
          description="The Channel Store category for your listing"
          data={
            genresField.value && !(ROKU_GENRES as readonly string[]).includes(genresField.value)
              ? [...ROKU_GENRES, genresField.value]
              : [...ROKU_GENRES]
          }
          value={genresField.value || null}
          onChange={(v) => genresField.onChange(v ?? '')}
          onBlur={genresField.onBlur}
          searchable
          clearable
          error={errors.genres?.message}
          disabled={disabled}
        />
      </Stack>
    </form>
  );
}

// ── Internal: Store Listing Form (shared OTT listing fields) ──

interface StoreListingFormProps {
  appCfgId: number;
  app: AppCfgsDto;
  isAppActive: boolean;
}

function StoreListingForm({ appCfgId, app, isAppActive }: StoreListingFormProps) {
  const disabled = !isAppActive;
  const listing = app.listing;
  const upsertMutation = useUpsertAppListing(appCfgId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm<AppListingFormSchemaData>({
    resolver: zodResolver(appListingFormSchema),
    defaultValues: AppUtils.transformListingToFormData(listing),
  });

  useEffect(() => {
    reset(AppUtils.transformListingToFormData(listing));
  }, [listing, reset]);

  const handleSave = handleSubmit((data: AppListingFormSchemaData) => {
    const req = AppUtils.transformListingFormToUpsertReq(data as AppListingFormData);
    upsertMutation.mutate(req, {
      onSuccess: () => {
        reset(data);
        notifications.show({
          title: 'Saved',
          message: 'Store listing updated successfully.',
          color: 'green',
        });
      },
      onError: (err) => {
        notifications.show({ title: 'Error', message: err.message, color: 'red' });
      },
    });
  });

  const handleDiscard = useCallback(() => {
    reset(AppUtils.transformListingToFormData(listing));
  }, [listing, reset]);

  return (
    <form onSubmit={handleSave}>
      <FormSaveBar
        isDirty={isDirty}
        isPending={upsertMutation.isPending}
        onDiscard={handleDiscard}
        saveDisabled={disabled}
      />

      <Stack gap="lg" mt="md">
        <Text size="sm" c="dimmed">
          These details appear in app store listings and are shared across your app&apos;s TV
          platforms. Child-Targeted Content is set on the General tab.
        </Text>

        <AppListingFields control={control} errors={errors} disabled={disabled} />
      </Stack>
    </form>
  );
}
